import { defaultTo, map, path, pipe, take, pathOr, add, converge } from 'ramda';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { isEqual } from 'lodash';
import { getChartData, getMostSold, getTotal, sumQuantity } from '../itemsReport/selectors';
import { extractFilterIds, getBrandFiltersForApi, getServiceCategoryFilterForApi } from '../selectors';
import { getEndDate, getStartDate } from '../filters/selectors';
import { getActiveBrandId, getActiveShopId, getBarberUserId } from '../../../auth/selectors';
import { getShopFilterForApi } from '../filters/ShopFilter/selectors';

// create a "selector creator" that uses lodash.isEqual instead of ===
const createDeepEqualSelector = createSelectorCreator(
  defaultMemoize,
  isEqual
);

const mapItems = type => map(item => ({
  id: item.barberId,
  name: item.barberName,
  value: item.value,
  type,
}));

const getReport = (selector, type = 'money') => pipe(
  selector,
  defaultTo([]),
  take(5),
  mapItems(type),
);

const topGrossing = path(['reports', 'metrics', 'data', 'topGrossing']);
export const getTopGrossing = getReport(topGrossing);

const averagePricePerService = path(['reports', 'metrics', 'data', 'averagePricePerService']);
export const getAveragePricePerService = getReport(averagePricePerService);

const servicesBooked = path(['reports', 'metrics', 'data', 'servicesBooked']);
export const getServicesBooked = getReport(servicesBooked, 'number');

const productsSold = path(['reports', 'metrics', 'data', 'productsSold']);
export const getProductsSold = getReport(productsSold, 'number');

const noShows = path(['reports', 'metrics', 'data', 'noShowsByBarber']);
export const getNoShows = getReport(noShows, 'number');

const cancellationsByBarber = path(['reports', 'metrics', 'data', 'cancellationsByBarber']);
export const getCancellationsByBarber = getReport(cancellationsByBarber, 'number');

const discounts = path(['reports', 'metrics', 'data', 'discounts']);
export const getDiscounts = getReport(discounts);

const tips = path(['reports', 'metrics', 'data', 'tips']);
export const getTips = getReport(tips);

export const getServiceSalesGrossTotal = pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'grossTotal']);
export const getServiceSalesGrossRefunds = pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'grossRefunds']);
export const getServiceSalesGrossDiscounts = converge(
  add, [
    pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'grossDiscounts']),
    pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'grossPromo']),
  ]
);
export const getServiceSalesNetTotal = pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'netTotal']);
export const getServiceSalesFees = pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'fees']);
export const getServiceSalesTips = pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'tips']);
export const getServiceSalesTaxes = pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'tax']);
export const getServiceSalesTaxBreakdown = pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'taxes']);
export const getServiceSales = pathOr({}, ['reports', 'metrics', 'data', 'serviceSales']);

export const getServiceSalesTotalCollected = pathOr(
  0,
  ['reports', 'metrics', 'data', 'serviceSales', 'totalCollected'],
);

export const getServiceSalesLoanRepayment = pathOr(
  0,
  ['reports', 'metrics', 'data', 'serviceSales', 'loanRepaymentCharge'],
);

export const getProductSalesGrossTotal = pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'grossTotal']);
export const getProductSalesGrossRefunds = pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'grossRefunds']);
export const getProductSalesGrossDiscounts = converge(
  add, [
    pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'grossDiscounts']),
    pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'grossPromo']),
  ]
);
export const getProductSalesNetTotal = pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'netTotal']);
export const getProductSalesFees = pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'fees']);
export const getProductSalesTaxes = pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'tax']);
export const getProductSalesTaxBreakdown = pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'taxes']);
export const getProductSalesSupplyPrice = pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'supplyPrice']);
export const getProductSalesGrossProfit = pathOr(0, ['reports', 'metrics', 'data', 'productSales', 'grossProfit']);
export const getProductSales = pathOr({}, ['reports', 'metrics', 'data', 'productSales']);

export const getProductSalesTotalCollected = pathOr(
  0,
  ['reports', 'metrics', 'data', 'productSales', 'totalCollected'],
);

export const getProductSalesLoanRepayment = pathOr(
  0,
  ['reports', 'metrics', 'data', 'productSales', 'loanRepaymentCharge'],
);

export const getSalesGrossTotal = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'grossTotal']);
export const getSalesGrossRefunds = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'grossRefunds']);
export const getSalesGrossDiscounts = converge(
  add, [
    pathOr(0, ['reports', 'metrics', 'data', 'sales', 'grossDiscounts']),
    pathOr(0, ['reports', 'metrics', 'data', 'sales', 'grossPromo']),
  ]
);
export const getSalesNetTotal = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'netTotal']);
export const getSalesFees = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'fees']);
export const getSalesTotalCollected = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'totalCollected']);
export const getSalesTips = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'tips']);
export const getSalesTaxes = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'tax']);
export const getSalesTaxBreakdown = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'taxes']);
export const getSalesTotalCash = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'totalCash']);
export const getSalesTotalPos = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'totalPos']);
export const getSalesTotalSquire = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'totalSquire']);
export const getSalesTotalGiftCard = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'totalGiftCard']);
export const getSalesLoanRepayment = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'loanRepaymentCharge']);

const itemsServices = pathOr({}, ['reports', 'metrics', 'data', 'serviceItems']);
const itemsProducts = pathOr({}, ['reports', 'metrics', 'data', 'productItems']);
export const getItemsServiceChart = getChartData(itemsServices);

export const getItemsProductChart = getChartData(itemsProducts);

export const getItemsServiceTotal = getTotal(itemsServices);
export const getItemsProductTotal = getTotal(itemsProducts);

export const getItemsServiceQuantity = pipe(itemsServices, sumQuantity);
export const getItemsProductQuantity = pipe(itemsProducts, sumQuantity);

export const getItemsServiceMostBooked = pipe(itemsServices, getMostSold);
export const getItemsProductMostSold = pipe(itemsProducts, getMostSold);

export const getAllBarbersFilter = path(['reports', 'metrics', 'barberFilter']);
export const getActiveBarbersFilterIds = createSelector(
  getAllBarbersFilter,
  extractFilterIds
);

// use the new "selector creator" to create a selector
export const getActiveBarberFilterMemoized = createDeepEqualSelector(
  getActiveBarbersFilterIds,
  items => items,
);

export const getAreAllBarbersSelected = createSelector(
  getActiveBarberFilterMemoized,
  getAllBarbersFilter,
  (active, all) => active.length === all.length,
);

export const getBarberFilterForApi = createSelector(
  getAreAllBarbersSelected,
  getActiveBarberFilterMemoized,
  (isAllSelected, activeIds) => (isAllSelected ? [] : activeIds)
);

export const getGiftCardSalesGrossTotal = pathOr(0, ['reports', 'metrics', 'data', 'giftCardSales', 'grossTotal']);
export const getGiftCardSalesTotalCollected = pathOr(0, ['reports', 'metrics', 'data', 'giftCardSales', 'totalCollected']);

export const getCancellationsCount = pathOr(0, ['reports', 'metrics', 'data', 'cancellations', 'count']);
export const getCancellationsAmount = pathOr(0, ['reports', 'metrics', 'data', 'cancellations', 'amount']);

export const getNoShowsCount = pathOr(0, ['reports', 'metrics', 'data', 'noShows', 'count']);
export const getNoShowsAmount = pathOr(0, ['reports', 'metrics', 'data', 'noShows', 'amount']);

export const getGrossSecuredDepositsCount = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'securedDepositsCount']);
export const getGrossSecuredDepositsAmount = pathOr(0, ['reports', 'metrics', 'data', 'sales', 'securedDepositsAmount']);

export const getServiceSecuredDepositsCount = pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'securedDepositsCount']);
export const getServiceSecuredDepositsAmount = pathOr(0, ['reports', 'metrics', 'data', 'serviceSales', 'securedDepositsAmount']);

export const getUnchargedCount = pathOr(0, ['reports', 'metrics', 'data', 'uncharged', 'count']);
export const getUnchargedAmount = pathOr(0, ['reports', 'metrics', 'data', 'uncharged', 'amount']);

export const isDirty = pathOr(false, ['reports', 'metrics', 'isDirty']);
export const hasDateChanged = pathOr(false, ['reports', 'metrics', 'hasDateChanged']);

export const getNextDepositAmount = pathOr(0, ['reports', 'metrics', 'data', 'nextDeposit', 'Amount']);
export const getLastDepositAmount = pathOr(0, ['reports', 'metrics', 'data', 'lastDeposit', 'Amount']);
export const getLastDepositDate = pathOr(null, ['reports', 'metrics', 'data', 'lastDeposit', 'Date of Deposit']);

export const getSalesByBarberAndShop = pathOr([], ['reports', 'metrics', 'data', 'salesByBarberAndShop']);
export const getProductivityByBarber = pathOr({}, ['reports', 'metrics', 'data', 'productivityByBarber']);
export const getSecuredSalesByBarberAndShop = pathOr([], ['reports', 'metrics', 'data', 'securedSalesByBarberAndShop']);
export const getSalesByBarberAndShopWithProductivity = createSelector(
  getSalesByBarberAndShop,
  getProductivityByBarber,
  getSecuredSalesByBarberAndShop,
  (sales, productivity, securedSales) => sales.map(sale => ({
    ...sale,
    productivity: productivity[sale.barberId],
    ...securedSales.find(s => s.id === sale.barberId),
  }))
);

const getGrossSalesBreakdownValule =
  fieldName => pathOr(0, ['reports', 'metrics', 'data', 'grossSalesBreakdown', fieldName]);
export const getBreakdownNewMembershipCount = getGrossSalesBreakdownValule('newMembershipCount');
export const getBreakdownRecurringMembershipCount = getGrossSalesBreakdownValule('recurringMembershipCount');
export const getBreakdownTotal = getGrossSalesBreakdownValule('grossTotal');
export const getBreakdownNewMembershipTotal = getGrossSalesBreakdownValule('newMembershipGrossTotal');
export const getBreakdownRecurringMembershipTotal = getGrossSalesBreakdownValule('recurringMembershipGrossTotal');
export const getBreakdownProductTotal = getGrossSalesBreakdownValule('productGrossTotal');
export const getBreakdownServiceTotal = getGrossSalesBreakdownValule('serviceGrossTotal');

export const getFiltersForMetrics = state => ({
  fromDate: getStartDate(state),
  toDate: getEndDate(state),
  barberFilter: getBarberFilterForApi(state),
  serviceCategories: getServiceCategoryFilterForApi(state),
  productBrands: getBrandFiltersForApi(state),
  barberId: getBarberUserId(state),
  shopId: getActiveShopId(state),
  brandId: getActiveBrandId(state),
  shopFilter: getShopFilterForApi(state),
});
